import { useEffect } from 'react';
import type { DocumentVersionSummary, VersionVerificationResult } from '../../../types';
import MobileBottomSheet from './MobileBottomSheet';
import { APP } from '../tokens';
import { docVersionDebugLog } from '../../../lib/documentVersionDebug';

interface MobileVersionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: DocumentVersionSummary[];
  logicalDocumentId?: string | null;
  activeVersionId?: string | null;
  verifyResult?: VersionVerificationResult | null;
  onSelectVersion?: (version: DocumentVersionSummary) => void;
  onVerifyVersion?: (baseVersionId: string) => void;
}

function formatVersionLabel(version: DocumentVersionSummary, index: number) {
  const number = version.versionNumber ?? versionsFallbackNumber(index);
  return `v${number}`;
}

function versionsFallbackNumber(index: number) {
  return index + 1;
}

function isEditableCopyFromSignedPdf(version?: DocumentVersionSummary | null): boolean {
  return Boolean(
    version &&
      version.source === 'conversion' &&
      version.lineageKind === 'copy' &&
      version.changeSummary === 'Editable copy from signed PDF',
  );
}

function partitionVersionLineage(versions: DocumentVersionSummary[]) {
  const main: DocumentVersionSummary[] = [];
  const copies: DocumentVersionSummary[] = [];
  for (const v of versions) {
    if (v.lineageKind === 'copy') copies.push(v);
    else main.push(v);
  }
  return { main, copies };
}

export default function MobileVersionSheet({
  open,
  onOpenChange,
  versions,
  logicalDocumentId,
  activeVersionId,
  verifyResult,
  onSelectVersion,
  onVerifyVersion,
}: MobileVersionSheetProps) {
  const { main, copies } = partitionVersionLineage(versions);
  const verifyBaseId = (main[0] ?? versions[0])?.id;
  useEffect(() => {
    if (!logicalDocumentId) return;
    const mismatched = versions.filter((version) => (
      Boolean(version.projectId) && String(version.projectId) !== String(logicalDocumentId)
    ));
    if (mismatched.length === 0) return;
    console.warn('[MobileVersionSheet] Version-to-group mismatch detected', {
      logicalDocumentId,
      mismatchedVersionIds: mismatched.map((v) => v.id),
    });
  }, [logicalDocumentId, versions]);

  const renderVersionRow = (version: DocumentVersionSummary, index: number) => {
    const active = version.id === activeVersionId;
    const signed = version.source === 'signed';
    const editableCopy = isEditableCopyFromSignedPdf(version);
    const label = `${formatVersionLabel(version, index)}${editableCopy ? ' · Editable copy from signed PDF' : version.lineageKind === 'copy' ? ' · copy' : signed ? ' · Signed' : ''}`;
    return (
      <button
        key={version.id}
        type="button"
        onClick={() => {
          if (!version.fileId) return;
          docVersionDebugLog('row_click', {
            documentId: version.projectId ?? logicalDocumentId ?? null,
            versionId: version.id,
            fileId: version.fileId ?? null,
            type: version.source ?? null,
            versionLabel: `v${version.versionNumber ?? versionsFallbackNumber(index)}`,
            surface: 'mobile',
          });
          onSelectVersion?.(version);
          onOpenChange(false);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          width: '100%',
          border: `1px solid ${active ? '#D97645' : APP.border}`,
          borderRadius: 10,
          padding: 12,
          background: active ? '#D9764514' : APP.bgVersionCard,
          textAlign: 'left',
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontFamily: APP.fontFamily, fontSize: 13, fontWeight: 700, color: APP.textPrimary }}>
            {label}
          </span>
          <span style={{ fontFamily: APP.fontFamily, fontSize: 11, color: APP.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {editableCopy ? 'Editable copy from signed PDF' : version.changeSummary || version.fileName || 'Document version'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {signed && (
            <span
              style={{
                borderRadius: 999,
                backgroundColor: 'rgba(27, 122, 75, 0.10)',
                color: '#1B7A4B',
                border: '1px solid rgba(27, 122, 75, 0.20)',
                padding: '1px 6px',
                fontFamily: APP.fontFamily,
                fontSize: 10,
                fontWeight: 700,
                lineHeight: '14px',
              }}
            >
              Signed
            </span>
          )}
          {active && (
            <span style={{ fontFamily: APP.fontFamily, fontSize: 11, fontWeight: 700, color: '#D97645' }}>
              Active
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <MobileBottomSheet open={open} onOpenChange={onOpenChange} title="Versions" collapsedLabel="Versions" showCollapsedButton={false}>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          background: APP.bgContent,
        }}
      >
        {versions.length === 0 ? (
          <div style={{ color: APP.textSecondary, fontFamily: APP.fontFamily, fontSize: 13 }}>
            No versions yet.
          </div>
        ) : (
          <>
            {main.length > 0 && copies.length > 0 && (
              <div style={{ fontFamily: APP.fontFamily, fontSize: 11, fontWeight: 700, color: APP.textMuted }}>
                Versions
              </div>
            )}
            {main.map((version, i) => renderVersionRow(version, i))}
            {copies.length > 0 && (
              <>
                <div
                  style={{
                    fontFamily: APP.fontFamily,
                    fontSize: 11,
                    fontWeight: 700,
                    color: APP.textMuted,
                    marginTop: main.length > 0 ? 6 : 0,
                  }}
                >
                  Copies
                </div>
                {copies.map((version, i) => renderVersionRow(version, main.length + i))}
              </>
            )}
          </>
        )}
        {versions.length > 1 && onVerifyVersion && verifyBaseId && (
          <button
            type="button"
            onClick={() => onVerifyVersion(verifyBaseId)}
            style={{
              minHeight: 40,
              borderRadius: 10,
              border: `1px solid ${APP.border}`,
              background: APP.bgVersionCard,
              color: APP.textPrimary,
              fontFamily: APP.fontFamily,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Verify changes
          </button>
        )}
        {verifyResult && (
          <div style={{ border: `1px solid ${APP.border}`, borderRadius: 10, padding: 10, background: APP.bgVersionCard }}>
            <div style={{ fontFamily: APP.fontFamily, fontSize: 12, fontWeight: 700, color: APP.textPrimary }}>
              Verification
            </div>
            <div style={{ marginTop: 4, fontFamily: APP.fontFamily, fontSize: 11, color: APP.textSecondary, lineHeight: '15px' }}>
              {verifyResult.summary ?? 'Version verification is available for this document.'}
            </div>
          </div>
        )}
      </div>
    </MobileBottomSheet>
  );
}
